import React, { useState, useEffect, useCallback, useRef } from 'react';
import { copyToClipboard as copyToClipboardUtil } from '../utils/clipboard';

// Общая логика вкладок "Вывод" и "Пополнения" банка.
// Используется как внутри полной Админ-панели (Банк → Вывод/Пополнения),
// так и в урезанной панели "Банк" в боковом меню — чтобы не дублировать
// стейт и обработчики в двух местах.
function AdminBankRequests({ tgId, adminNickname, activeSubTab, canAct, focusRequestId, focusNonce }) {
  const [withdrawals, setWithdrawals] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const [withdrawRejectModal, setWithdrawRejectModal] = useState({ show: false, reqId: null });
  const [withdrawRejectReason, setWithdrawRejectReason] = useState('');
  const [withdrawRejectError, setWithdrawRejectError] = useState('');

  const [depositApproveModal, setDepositApproveModal] = useState({ show: false, reqId: null, amount: '' });
  const [depositRejectModal, setDepositRejectModal] = useState({ show: false, reqId: null });
  const [depositRejectReason, setDepositRejectReason] = useState('');
  const [depositActionError, setDepositActionError] = useState('');
  const [proofModal, setProofModal] = useState(null);

  const [wFilterModal, setWFilterModal] = useState(false);
  const [wFilters, setWFilters] = useState({ dateFrom: '', dateTo: '', nick: '', account: '', status: 'ALL' });

  // Подсветка конкретной заявки при переходе из журнала действий по клику на #ID.
  const [highlightId, setHighlightId] = useState(null);
  const focusRef = useRef(null);
  const [wNickFocus, setWNickFocus] = useState(false);
  const [wAccountFocus, setWAccountFocus] = useState(false);

  const showToastMsg = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const copyToClipboard = (text) => {
    copyToClipboardUtil(text, () => showToastMsg('Скопировано'), () => showToastMsg('Ошибка копирования', true));
  };

  const formatTime = (utcStr) => {
    if (!utcStr) return 'Нет данных';
    const date = new Date(utcStr.replace(' ', 'T') + 'Z');
    return date.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', dateStyle: 'short', timeStyle: 'short' });
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/withdrawals');
      const data = await res.json();
      setWithdrawals(prev => JSON.stringify(prev) !== JSON.stringify(data.withdrawals || []) ? data.withdrawals || [] : prev);
    } catch (err) {
      console.error('Ошибка загрузки выводов', err);
    }
    try {
      const res = await fetch('/api/admin/deposits');
      const data = await res.json();
      setDeposits(prev => JSON.stringify(prev) !== JSON.stringify(data.deposits || []) ? data.deposits || [] : prev);
    } catch (err) {
      console.error('Ошибка загрузки пополнений', err);
    }
  }, []);

  useEffect(() => {
    fetchData().finally(() => setLoading(false));
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleWithdrawalAction = async (requestId, action, reason = '') => {
    setWithdrawRejectError('');
    if (action === 'reject' && !reason.trim()) {
      setWithdrawRejectError('Причина обязательна');
      return;
    }
    try {
      const res = await fetch('/api/admin/withdrawals/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, request_id: requestId, action, reason })
      });
      if (res.ok) {
        setWithdrawRejectModal({ show: false, reqId: null });
        setWithdrawRejectReason('');
        fetchData();
      } else {
        setWithdrawRejectError((await res.json()).error);
      }
    } catch (e) { setWithdrawRejectError('Ошибка соединения'); }
  };

  const handleDepositAction = async (requestId, action, amount = '', reason = '') => {
    setDepositActionError('');
    if (action === 'reject' && !reason.trim()) {
      setDepositActionError('Причина обязательна');
      return;
    }
    if (action === 'approve' && (!amount || amount <= 0)) {
      setDepositActionError('Укажите корректную сумму');
      return;
    }
    try {
      const res = await fetch('/api/admin/deposits/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, request_id: requestId, action, amount, reason })
      });
      if (res.ok) {
        setDepositApproveModal({ show: false, reqId: null, amount: '' });
        setDepositRejectModal({ show: false, reqId: null });
        setDepositRejectReason('');
        fetchData();
      } else {
        setDepositActionError((await res.json()).error);
      }
    } catch (e) { setDepositActionError('Ошибка соединения'); }
  };

  // По приходу фокуса (новый nonce): сбрасываем фильтры (чтобы искомая заявка
  // точно была видна) и включаем подсветку. Через несколько секунд гаснет.
  useEffect(() => {
    if (!focusNonce || !focusRequestId) return;
    setWFilters({ dateFrom: '', dateTo: '', nick: '', account: '', status: 'ALL' });
    setHighlightId(String(focusRequestId));
    const t = setTimeout(() => setHighlightId(null), 3200);
    return () => clearTimeout(t);
  }, [focusNonce, focusRequestId]);

  // Как только искомая карточка отрисовалась — прокручиваем к ней.
  useEffect(() => {
    if (highlightId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, withdrawals, deposits, activeSubTab]);

  const isFocusHit = (r) => highlightId != null &&
    (String(r.request_id) === highlightId || String(r.receipt_id) === highlightId);

  const applyWFilters = (list) => list.filter(w => {
    if (wFilters.status !== 'ALL' && w.status !== wFilters.status) return false;
    if (wFilters.nick && !w.mc_nickname.toLowerCase().includes(wFilters.nick.toLowerCase())) return false;
    if (wFilters.account && String(w.account_number) !== wFilters.account) return false;
    if (wFilters.dateFrom) {
      const wDate = new Date(w.created_at.replace(' ', 'T') + 'Z');
      if (wDate < new Date(wFilters.dateFrom)) return false;
    }
    if (wFilters.dateTo) {
      const wDate = new Date(w.created_at.replace(' ', 'T') + 'Z');
      const toDate = new Date(wFilters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (wDate > toDate) return false;
    }
    return true;
  });

  const filteredWithdrawals = applyWFilters(withdrawals);
  const filteredDeposits = applyWFilters(deposits);

  if (loading) return <div className="card" style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка...</div>;

  return (
    <div>
      {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}

      {activeSubTab === 'withdrawals' && (
        <div key="req-withdrawals" className="tab-fade">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h4 style={{margin: 0}}>Заявления на вывод</h4>
            <button className="secondary-btn" style={{width: 'auto', padding: '8px 12px', fontSize: '14px'}} onClick={() => setWFilterModal(true)}>
              Фильтры
            </button>
          </div>

          {filteredWithdrawals.length === 0 ? (
            <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Заявлений нет</p>
          ) : (
            filteredWithdrawals.map(w => {
              let cardClass = 'card withdrawal-card';
              if (w.status === 'COMPLETED') cardClass += ' approved';
              if (w.status === 'REJECTED') cardClass += ' rejected';
              const hit = isFocusHit(w);
              if (hit) cardClass += ' op-history-highlight';

              return (
                <div key={w.id} ref={hit ? focusRef : null} className={cardClass}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px'}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                      <span className="selectable-text"
                        style={{fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline', color: 'var(--accent-dark)'}}
                        onClick={() => copyToClipboard(w.receipt_id || w.request_id)}
                      >
                        ID: {w.receipt_id || w.request_id}
                      </span>
                    </div>
                    <span style={{fontWeight: 'bold', color: w.status === 'COMPLETED' ? 'var(--success)' : (w.status === 'REJECTED' ? 'var(--danger)' : 'var(--warning)'), whiteSpace: 'nowrap', textAlign: 'right'}}>
                      {w.status === 'COMPLETED' ? '✅ Одобрено' : (w.status === 'REJECTED' ? '❌ Отклонено' : '⏳ В обработке')}
                    </span>
                  </div>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Игрок:</b> {w.mc_nickname} (Счет #{w.account_number})</p>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Сумма:</b> {w.amount} сц</p>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Куда:</b> {w.destination_type === 'MAIL' ? 'На почту' : 'На счет MP'} ({w.destination_value})</p>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Подано:</b> {formatTime(w.created_at)}</p>

                  {w.status !== 'PENDING' && (
                    <>
                      <p style={{margin: '5px 0', fontSize: '14px'}}><b>Закрыто:</b> {formatTime(w.closed_at)}</p>
                      <p style={{margin: '5px 0', fontSize: '14px'}}><b>Отработал:</b> {w.processed_by}</p>
                      {w.status === 'REJECTED' && <p style={{margin: '5px 0', fontSize: '14px', color: 'var(--danger)'}}><b>Причина:</b> {w.reject_reason}</p>}
                    </>
                  )}

                  {w.status === 'PENDING' && canAct && (
                    <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                      <button
                        className="primary-btn"
                        style={{background: 'var(--success)'}}
                        onClick={() => handleWithdrawalAction(w.request_id, 'approve')}
                      >
                        Одобрить
                      </button>
                      <button
                        className="primary-btn"
                        style={{background: 'var(--danger)'}}
                        onClick={() => setWithdrawRejectModal({ show: true, reqId: w.request_id })}
                      >
                        Отклонить
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {activeSubTab === 'deposits' && (
        <div key="req-deposits" className="tab-fade">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h4 style={{margin: 0}}>Заявления на пополнение</h4>
            <button className="secondary-btn" style={{width: 'auto', padding: '8px 12px', fontSize: '14px'}} onClick={() => setWFilterModal(true)}>
              Фильтры
            </button>
          </div>

          {filteredDeposits.length === 0 ? (
            <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Заявлений нет</p>
          ) : (
            filteredDeposits.map(d => {
              let cardClass = 'card withdrawal-card';
              if (d.status === 'COMPLETED') cardClass += ' approved';
              if (d.status === 'REJECTED') cardClass += ' rejected';
              const hit = isFocusHit(d);
              if (hit) cardClass += ' op-history-highlight';

              return (
                <div key={d.id} ref={hit ? focusRef : null} className={cardClass}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px'}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                      <span className="selectable-text"
                        style={{fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline', color: 'var(--accent-dark)'}}
                        onClick={() => copyToClipboard(d.receipt_id || d.request_id)}
                      >
                        ID: {d.receipt_id || d.request_id}
                      </span>
                    </div>
                    <span style={{fontWeight: 'bold', color: d.status === 'COMPLETED' ? 'var(--success)' : (d.status === 'REJECTED' ? 'var(--danger)' : 'var(--warning)'), whiteSpace: 'nowrap', textAlign: 'right'}}>
                      {d.status === 'COMPLETED' ? '✅ Одобрено' : (d.status === 'REJECTED' ? '❌ Отклонено' : '⏳ В обработке')}
                    </span>
                  </div>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Игрок:</b> {d.mc_nickname} (Счет #{d.account_number})</p>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Сумма:</b> {d.amount} сц</p>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Откуда:</b> {d.source_type === 'MAIL' ? 'С почты' : 'Со счета MP'} ({d.source_value})</p>
                  <p style={{margin: '5px 0', fontSize: '14px'}}><b>Подано:</b> {formatTime(d.created_at)}</p>

                  {d.proof_image && (
                    <button className="secondary-btn" style={{padding: '6px 12px', fontSize: '12px', marginTop: '5px', width: 'auto'}} onClick={() => setProofModal(d.proof_image)}>
                      Посмотреть скриншот
                    </button>
                  )}

                  {d.status !== 'PENDING' && (
                    <>
                      <p style={{margin: '5px 0', fontSize: '14px', marginTop: '10px'}}><b>Закрыто:</b> {formatTime(d.closed_at)}</p>
                      <p style={{margin: '5px 0', fontSize: '14px'}}><b>Отработал:</b> {d.processed_by}</p>
                      {d.status === 'REJECTED' && <p style={{margin: '5px 0', fontSize: '14px', color: 'var(--danger)'}}><b>Причина:</b> {d.reject_reason}</p>}
                    </>
                  )}

                  {d.status === 'PENDING' && canAct && (
                    <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                      <button
                        className="primary-btn"
                        style={{background: 'var(--success)'}}
                        onClick={() => setDepositApproveModal({ show: true, reqId: d.request_id, amount: d.amount })}
                      >
                        Одобрить
                      </button>
                      <button
                        className="primary-btn"
                        style={{background: 'var(--danger)'}}
                        onClick={() => setDepositRejectModal({ show: true, reqId: d.request_id })}
                      >
                        Отклонить
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Модалка фильтров заявлений на вывод/пополнение */}
      {wFilterModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Фильтры заявлений</h3>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>От даты</label>
            <input type="date" value={wFilters.dateFrom} onChange={e => setWFilters({...wFilters, dateFrom: e.target.value})} />

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>До даты</label>
            <input type="date" value={wFilters.dateTo} onChange={e => setWFilters({...wFilters, dateTo: e.target.value})} />

            <div className="search-container">
              <label style={{fontSize: '14px', fontWeight: 'bold'}}>Никнейм</label>
              <input
                type="text"
                placeholder="Ник"
                value={wFilters.nick}
                onChange={e => setWFilters({...wFilters, nick: e.target.value})}
                onFocus={() => setWNickFocus(true)}
                onBlur={() => setTimeout(() => setWNickFocus(false), 200)}
              />
              {wNickFocus && Array.from(new Set([...withdrawals, ...deposits].map(w => w.mc_nickname))).filter(n => n.toLowerCase().includes(wFilters.nick.toLowerCase())).length > 0 && (
                <div className="search-dropdown">
                  {Array.from(new Set([...withdrawals, ...deposits].map(w => w.mc_nickname)))
                    .filter(n => n.toLowerCase().includes(wFilters.nick.toLowerCase()))
                    .map((n, idx) => (
                      <div key={idx} className="search-item" onClick={() => setWFilters({...wFilters, nick: n})}>
                        {n}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="search-container">
              <label style={{fontSize: '14px', fontWeight: 'bold'}}>Счет</label>
              <input
                type="number"
                placeholder="Номер счета"
                value={wFilters.account}
                onChange={e => setWFilters({...wFilters, account: e.target.value})}
                onFocus={() => setWAccountFocus(true)}
                onBlur={() => setTimeout(() => setWAccountFocus(false), 200)}
              />
              {wAccountFocus && Array.from(new Set([...withdrawals, ...deposits]
                .filter(w => wFilters.nick ? w.mc_nickname.toLowerCase() === wFilters.nick.toLowerCase() : true)
                .map(w => String(w.account_number))))
                .filter(a => a.includes(wFilters.account)).length > 0 && (
                <div className="search-dropdown">
                  {Array.from(new Set([...withdrawals, ...deposits]
                    .filter(w => wFilters.nick ? w.mc_nickname.toLowerCase() === wFilters.nick.toLowerCase() : true)
                    .map(w => String(w.account_number))))
                    .filter(a => a.includes(wFilters.account))
                    .map((a, idx) => (
                      <div key={idx} className="search-item" onClick={() => setWFilters({...wFilters, account: a})}>
                        #{a}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Статус</label>
            <select value={wFilters.status} onChange={e => setWFilters({...wFilters, status: e.target.value})}>
              <option value="ALL">Все</option>
              <option value="PENDING">В обработке</option>
              <option value="COMPLETED">Одобрено</option>
              <option value="REJECTED">Отклонено</option>
            </select>

            <div className="modal-actions">
              <button className="primary-btn" onClick={() => setWFilterModal(false)}>Применить</button>
              <button className="secondary-btn" onClick={() => { setWFilters({dateFrom: '', dateTo: '', nick: '', account: '', status: 'ALL'}); setWFilterModal(false); }}>Сбросить</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка отклонения вывода */}
      {withdrawRejectModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Отклонение вывода</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Укажите причину отказа. Средства будут возвращены на счет игрока.</p>
            <input
              type="text"
              placeholder="Причина отказа..."
              value={withdrawRejectReason || ''}
              onChange={e => setWithdrawRejectReason(e.target.value)}
            />
            {withdrawRejectError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{withdrawRejectError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} onClick={() => handleWithdrawalAction(withdrawRejectModal.reqId, 'reject', withdrawRejectReason)}>Отклонить</button>
              <button className="secondary-btn" onClick={() => { setWithdrawRejectModal({show: false, reqId: null}); setWithdrawRejectReason(''); setWithdrawRejectError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалки пополнения */}
      {depositApproveModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Одобрение пополнения</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Вы можете изменить сумму к зачислению, если она отличается от заявленной.</p>
            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Сумма к зачислению</label>
            <input
              type="number"
              value={depositApproveModal.amount}
              onChange={e => setDepositApproveModal({...depositApproveModal, amount: e.target.value})}
            />
            {depositActionError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{depositActionError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--success)'}} onClick={() => handleDepositAction(depositApproveModal.reqId, 'approve', depositApproveModal.amount)}>Одобрить</button>
              <button className="secondary-btn" onClick={() => { setDepositApproveModal({show: false, reqId: null, amount: ''}); setDepositActionError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {depositRejectModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Отклонение пополнения</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Укажите причину отказа.</p>
            <input
              type="text"
              placeholder="Причина отказа..."
              value={depositRejectReason || ''}
              onChange={e => setDepositRejectReason(e.target.value)}
            />
            {depositActionError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{depositActionError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} onClick={() => handleDepositAction(depositRejectModal.reqId, 'reject', '', depositRejectReason)}>Отклонить</button>
              <button className="secondary-btn" onClick={() => { setDepositRejectModal({show: false, reqId: null}); setDepositRejectReason(''); setDepositActionError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка просмотра скриншота */}
      {proofModal && (
        <div className="modal-overlay" onClick={() => setProofModal(null)}>
          <div className="modal" style={{maxWidth: '90%', padding: '10px'}} onClick={e => e.stopPropagation()}>
            <img src={proofModal} alt="Скриншот" style={{width: '100%', borderRadius: '8px'}} />
            <button className="secondary-btn" style={{marginTop: '10px'}} onClick={() => setProofModal(null)}>Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBankRequests;
